import { cloneDeep, groupBy, sortBy } from "lodash";

import { PlanningType, type Planning } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { m } from "~/paraglide/messages";

import { hourEndToMinutes, toMinutes, toTimeString } from "./time.helper";

/**
 * Merges timeslots within the same day by handling overlaps and respecting
 * the priority of user-defined timeslots.
 *
 * MAIN SCENARIOS:
 *
 * 1. NO OVERLAP (currentStart >= lastEnd)
 *    - Adjacent timeslots of the same type: merge them
 *    - Separate timeslots or different types: add the new timeslot
 *
 * 2. OVERLAP (currentStart < lastEnd)
 *    2.1. User-defined timeslot (current.userDefined = true)
 *         - Same type and userDefined: merge timeslots
 *         - Different types: user-defined slot takes priority
 *    2.2. Non-user-defined timeslot (current.userDefined = false)
 *         - Only the remaining (non-overlapping) portion is kept
 */
export const mergeTimeslotByDay = (timeslots: Planning[]): Planning[] => {
  if (timeslots.length === 0) return [];
  if (timeslots.length === 1) return timeslots;

  // Clone the timeslots to avoid mutating the original array
  const clonedTimeslots = cloneDeep(timeslots);
  // Sort the timeslots by start hour and then by end hour
  const sortedTimeslots = sortBy(clonedTimeslots, ["hourStart", "hourEnd"]);
  const [first, ...rest] = sortedTimeslots;
  if (!first) return [];

  const mergedPeriods: Planning[] = [first];

  for (const current of rest) {
    const last = mergedPeriods.at(-1);
    if (!last) break;

    // Convert hours to minutes for easier comparisons
    const currentStart = toMinutes(current.hourStart);
    const currentEnd = hourEndToMinutes(current.hourEnd);
    const lastStart = toMinutes(last.hourStart);
    const lastEnd = hourEndToMinutes(last.hourEnd);

    if (currentStart < lastEnd) {
      // ===== CASE 2 : Overlap =====
      if (current.userDefined) {
        if (last.userDefined && current.type === last.type) {
          // Same type and both userDefined
          // Example: [08:00-12:00 comfort user] + [10:00-14:00 comfort user] = [08:00-14:00 comfort user]
          const mergedStart = Math.min(lastStart, currentStart);
          const mergedEnd = Math.max(lastEnd, currentEnd);
          last.hourStart = toTimeString(mergedStart);
          last.hourEnd = toTimeString(mergedEnd);
        } else {
          // Different types or different userDefined
          // User-defined slot has priority and replaces the overlapping part
          if (currentEnd < lastEnd) {
            // User-defined slot ends before the existing slot
            // Example: [08:00-16:00 comfort] + [10:00-12:00 absence user] = [08:00-10:00 comfort] + [10:00-12:00 absence user] + [12:00-16:00 comfort]
            const newTimeslot = { ...last, hourStart: toTimeString(currentEnd) };
            if (currentStart > lastStart) {
              // User-defined slot starts after the start of the existing slot
              // Truncate the existing slot before the user-defined slot
              last.hourEnd = toTimeString(currentStart);
            } else {
              // User-defined slot starts at the beginning or before the existing slot
              // Remove the existing slot completely
              mergedPeriods.splice(mergedPeriods.length - 1, 1);
            }
            mergedPeriods.push(current);
            mergedPeriods.push(newTimeslot);
          } else {
            // User-defined slot ends after the existing slot
            // Example: [08:00-12:00 comfort] + [10:00-16:00 absence user] = [08:00-10:00 comfort] + [10:00-16:00 absence user]
            if (lastStart !== currentStart) {
              // User-defined slot does not start exactly at the beginning of the existing slot
              // Truncate the existing slot before the user-defined slot
              last.hourEnd = toTimeString(currentStart);
              mergedPeriods.push(current);
            } else {
              // User-defined slot starts exactly at the beginning of the existing slot
              // Replace the existing slot completely
              mergedPeriods.splice(mergedPeriods.length - 1, 1);
              mergedPeriods.push(current);
            }
          }
        }
      } else {
        // Non-user-defined slot
        // Non-user-defined slots have less priority
        if (currentEnd > lastEnd) {
          // Non-user-defined slot ends after the existing slot
          // Only the remaining (non-overlapping) portion is kept
          // Example: [08:00-12:00 comfort user] + [10:00-16:00 absence] = [08:00-12:00 comfort user] + [12:00-16:00 absence]
          mergedPeriods.push({ ...current, hourStart: toTimeString(lastEnd) });
        }
        // Non-user-defined slot ends before or at the end of the existing slot
        // It is completely absorbed and nothing is done
        // Example: [08:00-16:00 comfort user] + [10:00-12:00 absence] = [08:00-16:00 comfort user]
      }
    } else {
      // ===== CASE 1 : No overlap =====
      if (current.type === last.type && currentStart === lastEnd && current.userDefined === last.userDefined) {
        // Adjacent timeslots of the same type and same userDefined
        // Example: [08:00-12:00 comfort] + [12:00-16:00 comfort] = [08:00-16:00 comfort]
        last.hourEnd = current.hourEnd;
      } else {
        // Separate timeslots or different types/statuses
        // Example: [08:00-12:00 comfort] + [14:00-16:00 absence] = [08:00-12:00 comfort] + [14:00-16:00 absence]
        mergedPeriods.push(current);
      }
    }
  }

  return mergedPeriods;
};

/**
 * Merge user defined timeslots to avoid overlapping with other timeslots
 * @param timeslots timeslots to merge
 * @returns merged timeslots
 */
export const mergeTimeslot = (timeslots: Planning[]): Planning[] => {
  const timeslotsByDay = groupBy(timeslots, "day");
  return Object.values(timeslotsByDay).flatMap(mergeTimeslotByDay);
};

/**
 * Organizes plannings into week days based on the local start of the week,
 * and returns both the reordered day numbers and a mapping from day number to plannings.
 *
 * @template T - Planning type with at least a `day` property (string, english names lowercased: "monday", etc.)
 * @param plannings - Array of planning objects.
 * @returns An object with two properties:
 *   - reorderedDays: array of day numbers (0 = Sunday, 1 = Monday, etc.) starting from the locale's first day
 *   - timeslotsByDay: mapping of day number to an array of plannings for that day
 */
export const organizePlanningsByWeekDays = <T extends { day: string }>(
  plannings: T[],
): { reorderedDays: number[]; timeslotsByDay: Record<number, T[]> } => {
  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek();
  const daysOfWeek = [...Array(7).keys()];
  const reorderedDays = [...daysOfWeek.slice(firstDayOfWeek), ...daysOfWeek.slice(0, firstDayOfWeek)];

  const getEnglishDayName = (dayNumber: number) => dayjs().locale("en").day(dayNumber).format("dddd").toLowerCase();

  const planningsByEnglishDay = plannings.reduce<Record<string, T[]>>((acc, planning) => {
    (acc[planning.day] ??= []).push(planning);
    return acc;
  }, {});

  const timeslotsByDay = reorderedDays.reduce<Record<number, T[]>>((acc, dayNumber) => {
    const englishDayName = getEnglishDayName(dayNumber);
    acc[dayNumber] = planningsByEnglishDay[englishDayName] ?? [];
    return acc;
  }, {});

  return { reorderedDays, timeslotsByDay };
};

export const planningTypeKeyMessages: Record<PlanningType, string> = {
  [PlanningType.OCCUPATION]: m.occupation(),
  [PlanningType.INOCCUPATION]: m.inoccupation(),
  [PlanningType.THERMAL_SHOCK]: m.thermal_shock(),
  [PlanningType.COMFORT]: m.comfort(),
  [PlanningType.NIGHT]: m.night(),
  [PlanningType.ABSENCE]: m.absence(),
  [PlanningType.PRE_COMFORT]: m.pre_comfort(),
  [PlanningType.STOP]: m.holidays(),
  [PlanningType.ENABLED]: m.enabled(),
};
